/**
 * Schema checks for content-pack entities, shared by the build-time validator
 * (`scripts/validate-content.mjs`) and the runtime pack pipeline so both enforce
 * the SAME schema — no drift between what a DM validates locally and what a
 * subscriber's app trusts on fetch.
 *
 * Pure: every checker reports problems into an injected {@link ErrorSink} and does
 * no I/O. The logic is ported verbatim from the original validate-content.mjs
 * checks; the only change is that the error collector is passed in rather than
 * closed over, so the same functions serve a Node script and the browser.
 *
 * Inputs are parsed-but-untrusted JSON, hence the loose `Record<string, unknown>`
 * typing: validating shape IS the job, so we can't assume it up front.
 */

/** Records a problem, tagged with where it was found (e.g. `"class barbarian"`). */
export type ErrorSink = (where: string, msg: string) => void

type Obj = Record<string, unknown>

const isStr = (v: unknown): v is string => typeof v === 'string' && v.length > 0
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const isObj = (v: unknown): v is Obj => !!v && typeof v === 'object' && !Array.isArray(v)
/** Coerce an unknown to an array of objects for safe iteration (non-arrays → [], entries that are no objects left out). */
const asArr = (v: unknown): Obj[] => (Array.isArray(v) ? v.filter(isObj) : [])
/**
 * {@link asArr}, saying so for every entry it leaves out. JSON has `null`, and a hand-edited pack
 * gets one easily: that has to be a complaint, never a crash of the checker reading `.id` off it.
 */
function entries(err: ErrorSink, where: string, what: string, v: unknown): Obj[] {
  if (Array.isArray(v)) v.forEach((x, i) => { if (!isObj(x)) err(where, `${what}[${i}] must be an object, not ${JSON.stringify(x)}`) })
  return asArr(v)
}

export const CHOICE_TYPES = ['list', 'fightingStyle', 'invocations', 'weaponMastery', 'expertise', 'skill', 'feat', 'spells', 'language']
export const LEVEL_CHOICE_TYPES = [...CHOICE_TYPES, 'asiOrFeat', 'subclass', 'cantrip', 'spell', 'classChoice']
export const FREE_CASTS = ['shortRest', 'longRest', 'atWill']
export const FEAT_CATEGORIES = ['Origin', 'General', 'Fighting Style', 'Epic Boon']
export const ABILITIES = ['str', 'dex', 'con', 'int', 'wis', 'cha']
/** Skill ids (`Skill` in the character types; listed here because this file imports nothing, so the Node script can load it). */
const SKILLS = [
  'acrobatics', 'animalHandling', 'arcana', 'athletics', 'deception', 'history', 'insight', 'intimidation', 'investigation',
  'medicine', 'nature', 'perception', 'performance', 'persuasion', 'religion', 'sleightOfHand', 'stealth', 'survival',
]
const CASTER_TYPES = ['full', 'half', 'pact', 'third']
const isStrList = (v: unknown) => Array.isArray(v) && v.every((x) => typeof x === 'string')

/**
 * The multiclassing fields of a class (`multiclassAbilities`, `multiclassAbilityMode`,
 * `multiclassProficiencies`) and `casterType`, which a subclass option may carry too. A typo in
 * any of them fails silently otherwise: no prerequisite, no proficiencies, no share in the
 * Multiclass Spellcaster table.
 */
function checkMulticlass(err: ErrorSink, where: string, c: Obj) {
  if (c.casterType !== undefined && !CASTER_TYPES.includes(c.casterType as string))
    err(where, `casterType must be one of ${CASTER_TYPES.join('|')}`)
  if (c.multiclassAbilityMode !== undefined && !['all', 'any'].includes(c.multiclassAbilityMode as string))
    err(where, `multiclassAbilityMode must be 'all' or 'any'`)
  const abilities = c.multiclassAbilities
  if (abilities !== undefined && !(Array.isArray(abilities) && abilities.every((r: Obj) => r && ABILITIES.includes(r.ability as string) && isNum(r.min))))
    err(where, `multiclassAbilities must be [{ ability: ${ABILITIES.join('|')}, min: number }]`)
  const mc = c.multiclassProficiencies
  if (mc === undefined) return
  if (!mc || typeof mc !== 'object' || Array.isArray(mc)) return err(where, 'multiclassProficiencies must be an object')
  const lists = ['armorProficiencies', 'weaponProficiencies', 'toolProficiencies']
  for (const [key, value] of Object.entries(mc as Obj)) {
    if (lists.includes(key)) {
      if (!isStrList(value)) err(where, `multiclassProficiencies.${key} must be an array of names`)
    } else if (key === 'skillChoices') {
      const sc = value as Obj
      if (!sc || !isNum(sc.count) || !isStrList(sc.options)) err(where, 'multiclassProficiencies.skillChoices must be { count: number, options: [skill ids] }')
      else for (const id of sc.options as string[]) if (!SKILLS.includes(id)) err(where, `multiclassProficiencies.skillChoices: unknown skill '${id}'`)
    } else err(where, `multiclassProficiencies: unknown field '${key}' (use ${[...lists, 'skillChoices'].join(', ')})`)
  }
}

/** An https link the app may render; anything else (javascript:, data:, plain http) is refused. */
export function isHttpsUrl(v: unknown): v is string {
  if (!isStr(v)) return false
  try {
    return new URL(v).protocol === 'https:'
  } catch {
    return false
  }
}

/** One plain address: no spaces, no second `@`, no query tricks for a `mailto:` link. */
export const isEmail = (v: unknown): v is string => isStr(v) && /^[^\s@?&#]+@[^\s@?&#]+\.[^\s@?&#]+$/.test(v)

/** A pack's `author`: a name to credit, plus an optional https `url` and `email` to reach them. */
export function checkAuthor(err: ErrorSink, where: string, author: unknown) {
  if (!author || typeof author !== 'object' || Array.isArray(author)) {
    err(where, 'must be an object like { "name": "…" }')
    return
  }
  const a = author as Obj
  if (!isStr(a.name)) err(where, 'needs a name')
  if (a.url !== undefined && !isHttpsUrl(a.url)) err(where, "'url' must be a full https:// link")
  if (a.email !== undefined && !isEmail(a.email)) err(where, "'email' must be a single email address")
  for (const key of Object.keys(a)) {
    if (!['name', 'url', 'email'].includes(key)) err(where, `unknown field '${key}' (use name, url, email)`)
  }
}

/** `formerIds` (a renamed entry's old ids) must be a list of ids. */
export function checkFormerIds(err: ErrorSink, where: string, e: Obj) {
  if (e.formerIds !== undefined && !(Array.isArray(e.formerIds) && e.formerIds.every(isStr))) err(where, `'formerIds' must be an array of id strings`)
}

/** A spell's `upcast`: `{ dice: "1d8", above?: 1 }`. */
export function checkUpcast(err: ErrorSink, where: string, u: unknown) {
  if (u === undefined) return
  const o = u as Obj
  const ok = !!u && typeof u === 'object' && !Array.isArray(u) && isStr(o.dice) && /^\d+d\d+$/i.test(o.dice.trim()) &&
    (o.above === undefined || (isNum(o.above) && o.above >= 0 && o.above <= 9)) &&
    Object.keys(o).every((k) => k === 'dice' || k === 'above')
  if (!ok) err(where, `upcast must be { dice: "XdY", above?: slot level 0-9 }`)
}

/** Equipment's `attackAbility`: 'spellcasting' or an ability key, and only on a weapon. */
export function checkAttackAbility(err: ErrorSink, where: string, e: Obj) {
  const aa = e.attackAbility
  if (aa === undefined) return
  if (!isStr(aa) || !['spellcasting', ...ABILITIES].includes(aa))
    err(where, `attackAbility must be 'spellcasting' or one of ${ABILITIES.join('|')}`)
  else if (e.type !== 'weapon')
    err(where, `attackAbility only applies to a weapon (type is '${String(e.type)}')`)
}

export function checkColumn(err: ErrorSink, where: string, name: string, col: unknown) {
  if (col === undefined) return
  if (!Array.isArray(col) || col.length !== 20 || !col.every(isNum))
    err(where, `${name} must be an array of 20 numbers (one per character level)`)
}

export function checkOptions(err: ErrorSink, where: string, options: unknown) {
  if (options === undefined) return
  if (!Array.isArray(options)) return err(where, 'options must be an array')
  for (const o of entries(err, where, 'options', options)) {
    if (!isStr(o.id) || !isStr(o.name)) err(where, `option ${JSON.stringify(o.id ?? o.name)} needs id and name`)
    if (o.minLevel !== undefined && !isNum(o.minLevel)) err(where, `option ${o.id}: minLevel must be a number`)
    checkFeature(err, where, o)
  }
}

export function checkChoice(err: ErrorSink, where: string, c: Obj) {
  if (!isStr(c.id)) err(where, 'choice needs an id')
  if (!CHOICE_TYPES.includes(c.type as string)) err(where, `choice ${c.id}: unknown type '${c.type}'`)
  if (!isNum(c.choose)) err(where, `choice ${c.id}: 'choose' must be a number`)
  if (c.minLevel !== undefined && !isNum(c.minLevel)) err(where, `choice ${c.id}: minLevel must be a number`)
  checkColumn(err, `${where} choice ${c.id}`, 'countByLevel', c.countByLevel)
  if (c.featCategory !== undefined && !FEAT_CATEGORIES.includes(c.featCategory as string))
    err(where, `choice ${c.id}: unknown featCategory '${c.featCategory}'`)
  checkOptions(err, `${where} choice ${c.id}`, c.options)
  const opts = c.options
  if ((c.type === 'list' || c.type === 'invocations') && !(Array.isArray(opts) && opts.length))
    err(where, `choice ${c.id}: a 'list' choice needs inline options`)
  if (c.swapOnLevelUp !== undefined && !(isNum(c.swapOnLevelUp) && c.swapOnLevelUp >= 1))
    err(where, `choice ${c.id}: swapOnLevelUp must be a number of at least 1`)
  for (const key of ['includeTools', 'distinctPerRepeat'])
    if (c[key] !== undefined && typeof c[key] !== 'boolean') err(where, `choice ${c.id}: ${key} must be true or false`)
  if (c.subspecies !== undefined && !(Array.isArray(c.subspecies) && c.subspecies.every(isStr)))
    err(where, `choice ${c.id}: subspecies must be an array of lineage ids`)
  if (c.changeOnRest !== undefined && !['short', 'long'].includes(c.changeOnRest as string))
    err(where, `choice ${c.id}: changeOnRest must be 'short' or 'long'`)
  const rq = c.requires as Obj | undefined
  if (rq !== undefined && !(rq && isStr(rq.choice) && isStr(rq.option)))
    err(where, `choice ${c.id}: requires must be { choice: <choice key>, option: <option id> }`)
  const so = c.spellOptions as Obj | undefined
  if (so !== undefined) {
    if (!so || typeof so !== 'object' || Array.isArray(so)) err(where, `choice ${c.id}: spellOptions must be an object`)
    else {
      if (so.from !== undefined && !(Array.isArray(so.from) && so.from.every(isStr)))
        err(where, `choice ${c.id}: spellOptions.from must be an array of class names`)
      if (so.levels !== undefined && !(Array.isArray(so.levels) && so.levels.every((n) => isNum(n) && n >= 0 && n <= 9)))
        err(where, `choice ${c.id}: spellOptions.levels must be spell levels 0-9`)
      if (so.abilityFrom !== undefined && !isStr(so.abilityFrom))
        err(where, `choice ${c.id}: spellOptions.abilityFrom must be a choice id`)
      if (so.freeCast !== undefined && !FREE_CASTS.includes(so.freeCast as string))
        err(where, `choice ${c.id}: spellOptions.freeCast must be one of ${FREE_CASTS.join(' | ')}`)
    }
  }
}

/** A feature's or trait's structured effects: spell grants, speed, hit points, saves, ability scores. */
export function checkFeature(err: ErrorSink, where: string, f: Obj) {
  const name = `feature ${JSON.stringify(f.name)}`
  const ap = f.alwaysPrepared
  if (ap !== undefined) {
    const ok = Array.isArray(ap) && ap.every((g) =>
      g && typeof g === 'object' && Array.isArray(g.spells) && g.spells.every(isStr) && (g.minLevel === undefined || isNum(g.minLevel)) &&
      (g.freeCast === undefined || FREE_CASTS.includes(g.freeCast)) && (g.freeCastResource === undefined || isStr(g.freeCastResource)) &&
      (g.replacedBy === undefined || isStr(g.replacedBy)))
    if (!ok) err(where, `${name}: alwaysPrepared must be an array of { minLevel?: number, spells: [names], freeCast?: ${FREE_CASTS.join(' | ')}, freeCastResource?: key }`)
  }
  const tables = f.tables
  if (tables !== undefined) {
    const ok = Array.isArray(tables) && tables.every((t) =>
      t && isStr(t.caption) && Array.isArray(t.columns) && t.columns.every(isStr) &&
      Array.isArray(t.rows) && t.rows.every((row: unknown) => Array.isArray(row) && row.length === t.columns.length && row.every((c) => typeof c === 'string')))
    if (!ok) err(where, `${name}: tables must be [{ caption, columns: [titles], rows: [[one cell per column]] }]`)
  }
  for (const res of entries(err, `${where} ${name}`, 'resources', f.resources)) checkResource(err, `${where} ${name}`, res)
  const xl = f.extraSpellLists
  if (xl !== undefined && !(Array.isArray(xl) && xl.every(isStr)))
    err(where, `${name}: extraSpellLists must be an array of class names`)
  const sp = f.speed as Obj | undefined
  if (sp !== undefined) {
    const steps = sp?.byLevel
    const ok =
      sp && typeof sp === 'object' && !Array.isArray(sp) &&
      (sp.bonus === undefined || isNum(sp.bonus)) &&
      (steps === undefined || (Array.isArray(steps) && steps.every((x) => Array.isArray(x) && x.length === 2 && x.every(isNum)))) &&
      (sp.unless === undefined || ['heavyArmor', 'armorOrShield'].includes(sp.unless as string)) &&
      (sp.climb === undefined || typeof sp.climb === 'boolean') &&
      (sp.swim === undefined || typeof sp.swim === 'boolean')
    if (!ok) err(where, `${name}: speed must be { bonus?: number, byLevel?: [level, bonus][], unless?: 'heavyArmor' | 'armorOrShield', climb?: boolean, swim?: boolean }`)
  }
  const hp = f.hitPoints as Obj | undefined
  if (hp !== undefined && !(hp && typeof hp === 'object' && isNum(hp.perLevel)))
    err(where, `${name}: hitPoints must be { perLevel: number }`)
  const saves = f.savingThrows
  if (saves !== undefined && !(Array.isArray(saves) && saves.every((a) => ABILITIES.includes(a as string))))
    err(where, `${name}: savingThrows must be an array of ${ABILITIES.join('|')}`)
  const as = f.abilityScores as Obj | undefined
  if (as !== undefined) {
    const inc = as?.increase as Obj | undefined
    const ok = as && isNum(as.max) && inc && typeof inc === 'object' &&
      Object.entries(inc).every(([k, v]) => ABILITIES.includes(k) && isNum(v))
    if (!ok) err(where, `${name}: abilityScores must be { increase: { <ability>: number }, max: number }`)
  }
  for (const key of ['armorProficiencies', 'weaponProficiencies', 'languages']) {
    const v = f[key]
    if (v !== undefined && !(Array.isArray(v) && v.every(isStr))) err(where, `${name}: ${key} must be an array of names`)
  }
  const wa = f.weaponAbility as Obj | undefined
  if (wa !== undefined && !(wa && ABILITIES.includes(wa.ability as string) && Array.isArray(wa.weapons) && wa.weapons.every(isStr) &&
      (wa.unless === undefined || wa.unless === 'armorOrShield')))
    err(where, `${name}: weaponAbility must be { ability, weapons: [descriptions], unless?: 'armorOrShield' }`)
  const def = f.defenses
  if (def !== undefined && !(Array.isArray(def) && def.every((d) => isStr(d) || (d && isNum(d.minLevel) && isStr(d.text)))))
    err(where, `${name}: defenses must be an array of text or { minLevel: number, text }`)
  const se = f.senses
  if (se !== undefined && !(Array.isArray(se) && se.every((s) => s && isStr(s.sense) && isNum(s.range))))
    err(where, `${name}: senses must be an array of { sense, range: feet }`)
  for (const key of ['heroicInspirationOnLongRest', 'initiativeProficiency'])
    if (f[key] !== undefined && typeof f[key] !== 'boolean') err(where, `${name}: ${key} must be true or false`)
  if (f.rangedAttackBonus !== undefined && !isNum(f.rangedAttackBonus)) err(where, `${name}: rangedAttackBonus must be a number`)
  if (f.spellcastingAbility !== undefined && !ABILITIES.includes(f.spellcastingAbility as string))
    err(where, `${name}: spellcastingAbility must be one of ${ABILITIES.join('|')}`)
  if (f.halfProficiencyToSkills !== undefined && typeof f.halfProficiencyToSkills !== 'boolean')
    err(where, `${name}: halfProficiencyToSkills must be true or false`)
  if (f.extraCantrips !== undefined && !(isNum(f.extraCantrips) && f.extraCantrips >= 0))
    err(where, `${name}: extraCantrips must be a number of cantrips`)
  const sb = f.skillBonus as Obj | undefined
  if (sb !== undefined) {
    const ok = sb && Array.isArray(sb.skills) && sb.skills.every(isStr) && ABILITIES.includes(sb.ability as string) &&
      (sb.min === undefined || isNum(sb.min))
    if (!ok) err(where, `${name}: skillBonus must be { skills: [skill ids], ability: ${ABILITIES.join('|')}, min?: number }`)
  }
}

export function checkLevelChoice(err: ErrorSink, where: string, c: Obj, cls?: Obj) {
  if (!LEVEL_CHOICE_TYPES.includes(c.type as string)) err(where, `unknown level-choice type '${c.type}'`)
  if (c.type === 'classChoice') {
    if (!isStr(c.ref)) err(where, `classChoice needs a 'ref'`)
    else if (cls && ![...asArr(cls.levelOneChoices), ...subclassOptions(cls).flatMap((o) => asArr(o.levelOneChoices))].some((x) => x.id === c.ref))
      err(where, `classChoice ref '${c.ref}' doesn't match any of the class's choices`)
  }
  if (c.count !== undefined && !isNum(c.count)) err(where, `'count' must be a number`)
  if (c.asiFeat !== undefined && !isStr(c.asiFeat)) err(where, `'asiFeat' must be a feat id string`)
  checkOptions(err, where, c.options)
}

/** A class's subclass options (the options of its `subclass` level choices). */
function subclassOptions(cls: Obj): Obj[] {
  return asArr(cls.levels).flatMap((l) => asArr(l.choices)).filter((c) => c.type === 'subclass').flatMap((c) => asArr(c.options))
}

export function checkResource(err: ErrorSink, where: string, r: Obj) {
  if (!isStr(r.key) || !isStr(r.name)) err(where, `resource needs key and name`)
  if (!['short', 'long'].includes(r.reset as string)) err(where, `resource ${r.key}: reset must be 'short' or 'long'`)
  for (const k of ['shortRestRegain', 'shortRestFrom'])
    if (r[k] !== undefined && !(isNum(r[k]) && (r[k] as number) >= 1)) err(where, `resource ${r.key}: ${k} must be a number of at least 1`)
  const modes = ['byLevel', 'perLevel', 'ability', 'proficiencyBonus', 'uses'].filter((k) => r[k] !== undefined)
  if (modes.length !== 1) err(where, `resource ${r.key}: declare exactly one of byLevel | perLevel | ability | proficiencyBonus | uses`)
  if (r.proficiencyBonus !== undefined && r.proficiencyBonus !== true) err(where, `resource ${r.key}: proficiencyBonus must be true`)
  if (r.uses !== undefined && !(isNum(r.uses) && r.uses >= 1)) err(where, `resource ${r.key}: uses must be a number of at least 1`)
  const byLevel = r.byLevel
  if (byLevel && !(Array.isArray(byLevel) && byLevel.every((s) => Array.isArray(s) && s.length === 2 && s.every(isNum))))
    err(where, `resource ${r.key}: byLevel must be [level, value] pairs`)
}

export function checkClass(err: ErrorSink, where: string, c: Obj, { partial = false }: { partial?: boolean } = {}) {
  checkFormerIds(err, where, c)
  checkMulticlass(err, where, c)
  if (!partial) {
    for (const f of ['id', 'name']) if (!isStr(c[f])) err(where, `missing ${f}`)
    if (!isNum(c.hitDie)) err(where, 'hitDie must be a number')
  }
  for (const list of ['spellList', 'featList']) {
    if (c[list] === undefined) continue
    const sl = c[list] as Obj
    if (!sl || typeof sl !== 'object' || Array.isArray(sl)) err(where, `'${list}' must be an object`)
    else
      for (const key of ['from', 'add', 'remove']) {
        const v = sl[key]
        if (v !== undefined && !(Array.isArray(v) && v.every(isStr)))
          err(where, `${list}.${key} must be an array of names`)
      }
  }
  if (c.spellSlotsReset !== undefined && !['short', 'long'].includes(c.spellSlotsReset as string))
    err(where, `spellSlotsReset must be 'short' or 'long'`)
  checkColumn(err, where, 'cantripsByLevel', c.cantripsByLevel)
  checkColumn(err, where, 'preparedSpellsByLevel', c.preparedSpellsByLevel)
  for (const ch of entries(err, where, 'levelOneChoices', c.levelOneChoices)) checkChoice(err, where, ch)
  for (const r of entries(err, where, 'resources', c.resources)) checkResource(err, where, r)
  for (const f of entries(err, where, 'features', c.features)) checkFeature(err, where, f)
  for (const l of entries(err, where, 'levels', c.levels)) {
    const level = l.level
    if (!isNum(level) || level < 2 || level > 20) err(where, `level entry '${level}' out of range 2-20`)
    for (const ch of entries(err, `${where} L${level}`, 'choices', l.choices)) checkLevelChoice(err, `${where} L${level}`, ch, c)
    for (const f of entries(err, `${where} L${level}`, 'features', l.features)) checkFeature(err, `${where} L${level}`, f)
  }
  // A subclass carries its own features and choices, merged in once it's chosen.
  for (const o of subclassOptions(c)) {
    const at = `${where} subclass ${o.id}`
    for (const ch of entries(err, at, 'levelOneChoices', o.levelOneChoices)) checkChoice(err, at, ch)
    for (const l of entries(err, at, 'levels', o.levels)) {
      if (!isNum(l.level) || l.level < 1 || l.level > 20) err(at, `level entry '${l.level}' out of range 1-20`)
      for (const ch of entries(err, `${at} L${l.level}`, 'choices', l.choices)) checkLevelChoice(err, `${at} L${l.level}`, ch, c)
      for (const f of entries(err, `${at} L${l.level}`, 'features', l.features)) checkFeature(err, `${at} L${l.level}`, f)
    }
  }
}

/** A pack's subclass entry: the class it joins, a name or id, and its own levels and choices. */
export function checkSubclass(err: ErrorSink, where: string, s: Obj) {
  checkFormerIds(err, where, s)
  // A subclass may grant spellcasting its class lacks (a third caster): only `casterType` of these applies to it.
  checkMulticlass(err, where, { casterType: s.casterType })
  if (!isStr(s.class)) err(where, `needs 'class': the id or name of the class it belongs to`)
  if (!isStr(s.id) && !isStr(s.name)) err(where, 'needs a name (or id)')
  for (const ch of entries(err, where, 'levelOneChoices', s.levelOneChoices)) checkChoice(err, where, ch)
  for (const l of entries(err, where, 'levels', s.levels)) {
    if (!isNum(l.level) || l.level < 1 || l.level > 20) err(where, `level entry '${l.level}' out of range 1-20`)
    for (const ch of entries(err, `${where} L${l.level}`, 'choices', l.choices)) checkLevelChoice(err, `${where} L${l.level}`, ch)
    for (const f of entries(err, `${where} L${l.level}`, 'features', l.features)) checkFeature(err, `${where} L${l.level}`, f)
  }
}

export function checkSpecies(err: ErrorSink, where: string, r: Obj, { partial = false }: { partial?: boolean } = {}) {
  if (!partial && (!isStr(r.id) || !isStr(r.name))) err(where, 'species needs id and name')
  for (const ch of entries(err, where, 'choices', r.choices)) checkChoice(err, where, ch)
  for (const t of entries(err, where, 'traits', r.traits)) checkFeature(err, where, t)
  checkFormerIds(err, where, r)
  for (const sub of entries(err, where, 'subspecies', r.subspecies)) {
    checkFormerIds(err, `${where} ${sub.id}`, sub)
    for (const t of entries(err, `${where} ${sub.id}`, 'traits', sub.traits)) checkFeature(err, `${where} ${sub.id}`, t)
    const dropped = sub['-traits']
    if (dropped !== undefined && !(Array.isArray(dropped) && dropped.every(isStr)))
      err(`${where} ${sub.id}`, `'-traits' must be an array of the species' trait names`)
  }
}

export function checkFeat(err: ErrorSink, where: string, f: Obj, { partial = false }: { partial?: boolean } = {}) {
  checkFormerIds(err, where, f)
  if (!partial && !FEAT_CATEGORIES.includes(f.category as string)) err(where, `unknown category '${f.category}'`)
  if (f.prerequisite !== undefined)
    err(where, `'prerequisite' was removed: use minLevel / classes / species; put unmodeled requirements (ability scores, features) in the description`)
  if (f.minLevel !== undefined && !(isNum(f.minLevel) && f.minLevel >= 1 && f.minLevel <= 20))
    err(where, `minLevel must be a number between 1 and 20`)
  for (const key of ['classes', 'species']) {
    const v = f[key]
    if (v !== undefined && !(Array.isArray(v) && v.every(isStr))) err(where, `'${key}' must be an array of id strings`)
  }
  const abilities = f.abilities
  if (abilities !== undefined) {
    const ok =
      Array.isArray(abilities) && abilities.every((r) => r && ABILITIES.includes(r.ability as string) && isNum(r.min))
    if (!ok) err(where, `'abilities' must be an array of { ability: ${ABILITIES.join('|')}, min: number }`)
  }
  if (f.spellcasting !== undefined && typeof f.spellcasting !== 'boolean')
    err(where, `'spellcasting' must be a boolean`)
  if (f.repeatable !== undefined && typeof f.repeatable !== 'boolean') err(where, `'repeatable' must be a boolean`)
  checkFeature(err, where, f)
  for (const ch of entries(err, where, 'choices', f.choices)) checkChoice(err, where, ch)
}

/**
 * Validate an importable {@link import('../types/content').RawContentPack} — the
 * shape fetched from a subscription or uploaded by hand. Entries may be extensions
 * (`{ base }`); those are checked structurally only (`partial`), since their
 * missing fields are inherited from the base entity. Returns a flat list of
 * human-readable problems (`[]` = valid). This is the runtime counterpart to the
 * per-file checks the build script runs.
 */
export function validateRawPack(pack: unknown): string[] {
  const errors: string[] = []
  const err: ErrorSink = (where, msg) => errors.push(`${where}: ${msg}`)
  if (!pack || typeof pack !== 'object') {
    err('pack', 'must be a JSON object')
    return errors
  }
  const p = pack as Obj
  if (!isStr(p.id) || !isStr(p.name)) err('pack', 'pack needs id and name')
  if (p.author !== undefined) checkAuthor(err, 'pack author', p.author)
  for (const key of ['species', 'classes', 'subclasses', 'backgrounds', 'spells', 'equipment', 'feats']) {
    if (p[key] !== undefined && !Array.isArray(p[key])) err('pack', `'${key}' must be an array`)
  }
  for (const s of entries(err, 'pack', 'subclasses', p.subclasses)) checkSubclass(err, `subclass ${s.id ?? s.name}`, s)
  for (const c of entries(err, 'pack', 'classes', p.classes)) checkClass(err, `class ${c.id ?? c.name ?? c.base}`, c, { partial: !!c.base })
  for (const r of entries(err, 'pack', 'species', p.species)) checkSpecies(err, `species ${r.id ?? r.name ?? r.base}`, r, { partial: !!r.base })
  for (const f of entries(err, 'pack', 'feats', p.feats)) checkFeat(err, `feat ${f.id ?? f.name ?? f.base}`, f, { partial: !!f.base })
  for (const key of ['backgrounds', 'spells', 'equipment']) for (const e of entries(err, 'pack', key, p[key])) checkFormerIds(err, `${key} ${e.id ?? e.name ?? e.base}`, e)
  for (const e of asArr(p.spells)) checkUpcast(err, `spells ${e.id ?? e.name ?? e.base}`, e.upcast)
  for (const e of asArr(p.equipment)) checkAttackAbility(err, `equipment ${e.id ?? e.name ?? e.base}`, e)
  return errors
}

/**
 * Cross-entity check: every name in a class's `spellList` / `featList` must
 * resolve to a real spell, feat or class. Runs after packs are merged (a pack
 * may name an entry another pack provides), so a typo fails loudly instead of
 * silently dropping the entry.
 */
export function unresolvedListRefs(
  classes: RefClass[],
  spells: Named[],
  feats: Named[] = [],
): string[] {
  const key = (s: string) => s.toLowerCase().replace(/['\u2019]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  const keysOf = (list: Named[]) => new Set(list.flatMap((e) => [key(e.id), key(e.name)]))
  const classKeys = keysOf(classes)
  const pools = { spellList: keysOf(spells), featList: keysOf(feats) }
  const noun = { spellList: 'spell', featList: 'feat' }
  const problems: string[] = []
  for (const cls of classes)
    for (const list of ['spellList', 'featList'] as const) {
      for (const field of ['add', 'remove'] as const)
        for (const ref of cls[list]?.[field] ?? [])
          if (!pools[list].has(key(ref)))
            problems.push(`class ${cls.id}: ${list}.${field}: no ${noun[list]} named '${ref}'`)
      for (const ref of cls[list]?.from ?? [])
        if (!classKeys.has(key(ref)))
          problems.push(`class ${cls.id}: ${list}.from: no class named '${ref}'`)
    }
  // Spell grants on features and spell-type choices name spells and classes as printed.
  for (const cls of classes) {
    const subclasses = (cls.levels ?? []).flatMap((l) => l.choices ?? []).flatMap((c) => c.options ?? [])
    const standing = [...(cls.levelOneChoices ?? []), ...subclasses.flatMap((o) => o.levelOneChoices ?? [])]
    const features = [
      ...(cls.features ?? []),
      ...(cls.levels ?? []).flatMap((l) => l.features ?? []),
      ...subclasses.flatMap((o) => (o.levels ?? []).flatMap((l) => l.features ?? [])),
      // A choice's options can grant spells too (Circle of the Land's land types).
      ...standing.flatMap((c) => (c.options ?? []).map((o) => ({ ...o, name: `${c.id}: ${o.name}` }))),
    ]
    for (const f of features) {
      for (const group of f.alwaysPrepared ?? [])
        for (const ref of group.spells)
          if (!pools.spellList.has(key(ref)))
            problems.push(`class ${cls.id}: ${f.name}: alwaysPrepared: no spell named '${ref}'`)
      for (const ref of f.extraSpellLists ?? [])
        if (!classKeys.has(key(ref)))
          problems.push(`class ${cls.id}: ${f.name}: extraSpellLists: no class named '${ref}'`)
    }
    for (const ch of [...(cls.levelOneChoices ?? []), ...subclasses.flatMap((o) => o.levelOneChoices ?? [])])
      for (const ref of ch.spellOptions?.from ?? [])
        if (!classKeys.has(key(ref)))
          problems.push(`class ${cls.id}: choice ${ch.id}: spellOptions.from: no class named '${ref}'`)
  }
  return problems
}

/**
 * Cross-entity check: every name a lineage drops with `"-traits"` must match a trait
 * on its parent species. Runs on merged content, since the parent's traits may come
 * from the base of an extension, so a typo (or a base that renamed the trait) fails
 * loudly instead of quietly dropping nothing.
 */
export function unresolvedTraitDrops(species: DropSpecies[]): string[] {
  // Same loose match as the resolver's; spelled out here because this module takes no
  // imports (it is type-stripped into the pack repo's standalone validator).
  const key = (s: string) => s.toLowerCase().replace(/['\u2019]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  const problems: string[] = []
  for (const sp of species) {
    const traits = new Set((sp.traits ?? []).map((t) => key(t.name)))
    for (const sub of sp.subspecies ?? [])
      for (const ref of sub['-traits'] ?? [])
        if (!traits.has(key(ref)))
          problems.push(`species ${sp.id}: lineage ${sub.id}: '-traits': no ${sp.name} trait named '${ref}'`)
  }
  return problems
}

interface DropSpecies extends Named {
  traits?: { name: string }[]
  subspecies?: { id: string; '-traits'?: string[] }[]
}

interface Named { id: string; name: string }
type ForwardList = { from?: string[]; add?: string[]; remove?: string[] }
interface RefFeature { name: string; alwaysPrepared?: { spells: string[] }[]; extraSpellLists?: string[] }
type RefChoice = { id: string; spellOptions?: { from?: string[] }; options?: RefFeature[] }
interface RefClass extends Named {
  spellList?: ForwardList
  featList?: ForwardList
  features?: RefFeature[]
  levels?: {
    features?: RefFeature[]
    choices?: { options?: { levels?: { features?: RefFeature[] }[]; levelOneChoices?: RefChoice[] }[] }[]
  }[]
  levelOneChoices?: RefChoice[]
}
